import {  message, Upload, Modal, Button, Col, DatePicker, Form, Input, Row, Select, Space, Table, Typography, FormInstance } from 'antd';
import PageWithFilters from 'components/page/page-with-filters';
import { PageWithFiltersContentContainer } from 'components/page/page.styles';
import { format } from 'date-fns';
import { Dayjs } from 'dayjs';
import useGetBulkLoadStatus from 'hooks/data/useGetBulkLoadStatus';
import useUploadBulkFile from 'hooks/post_no_content/useUploadBulkFile';
import React, { useState } from 'react';
import type { ColumnsType } from 'antd/es/table';
import { getProperDate } from 'utils/date';
import { IProcessorStatus } from 'interfaces/processor-status';
import styled from 'styled-components';
import { UploadOutlined } from '@ant-design/icons';
import { useNavigate, useParams} from 'react-router-dom';
import useToastContext from '../../hooks/useToastContext';
import { InboxOutlined } from '@ant-design/icons';
import { HttpStatusCode } from '../../constants';
import type { GetProp, UploadFile, UploadProps } from 'antd';


const { Dragger } = Upload;
type FileType = Parameters<GetProp<UploadProps, 'beforeUpload'>>[0];


const BulkLoadActionsContainer = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-bottom: 1.5rem;
`;

const { RangePicker } = DatePicker;

enum ProcessStatus {
  PENDING = 'PENDING',
  PROCESSING = 'PROCESSING',
  PROCESSED = 'PROCESSED',
  ERROR = 'ERROR',
}

enum ImporterProcessEnum {
  CARDHOLDER_LOAD = 'CARDHOLDER_LOAD',
  CARD_LOAD = 'CARD_LOAD'
}

/*
*   Modal
*/

type NewBulkLoadFormInputs = {
  process_type: ImporterProcessEnum,

};

const SubmitButton = ({ form, loading }: { form: FormInstance; loading: boolean }) => {
  const [submittable, setSubmittable] = React.useState(false);

  // Watch all values
  const values = Form.useWatch([], form);

  React.useEffect(() => {
    form.validateFields({ validateOnly: true }).then(
      () => {
        setSubmittable(true);
      },
      () => {
        setSubmittable(false);
      }
    );
  }, [values]);

  return (
    <Col span={24} style={{ textAlign: 'center', marginTop: '1em'}}>
      <Button
        type="primary"
        htmlType="submit"
        disabled={!submittable}
        loading={loading}
      >
        {loading ? 'Uploading' : 'Start Upload'}
      </Button>
    </Col>
  );
};

const NewBulkLoadForm = ({ onCancel, onSubmit}: { onCancel: () => void, onSubmit: () => void}) => {
  const toast = useToastContext();
  const [form] = Form.useForm();

  const { upload, isLoading } = useUploadBulkFile();

  const [fileList, setFileList] = useState<UploadFile[]>([]);

  const props: UploadProps = {
    name: 'file',
    multiple: true,
    onDrop(e) {
      console.log('Dropped files', e.dataTransfer.files);
    },
    onRemove: (file) => {
      const index = fileList.indexOf(file);
      const newFileList = fileList.slice();
      newFileList.splice(index, 1);
      setFileList(newFileList);
    },
    beforeUpload: (file) => {
      setFileList([...fileList, file]);

      return false;
    },
    fileList,
  };

  const handleUpload = async () => {

    console.log("Upload files")
    fileList.forEach(async (file) => {
      console.log(file)
      const formData = new FormData();
      formData.append('file', file as FileType);

      try {
        const res = await upload( form.getFieldValue("process_type") , formData);
        if ([HttpStatusCode.NoContent, HttpStatusCode.Ok].includes(res.status)) {
          toast.success('Archivo subido', `El archivo se subio correctamente`);
          form.resetFields();
          onSubmit();
          onCancel();
        } else {
          throw new Error('Hubo en error al subir el archivo.');
        }
      } catch (e) {
        console.error(e);
        toast.error('Error!', 'Hubo en error al subir el archivo.');
      }

    });
  };


  return (
    <Form<NewBulkLoadFormInputs>
      name="basic"
      form={form}
      style={{ maxWidth: '100%', marginTop: '1rem' }}
      initialValues={{ remember: true }}
      onFinish={handleUpload}
      onFinishFailed={console.log}
      autoComplete="off"
    >
      <Typography.Title level={3}>Subir archivo</Typography.Title>
      <Form.Item<NewBulkLoadFormInputs> name="process_type">
        <Select
          placeholder="Seleccionar tipo de proceso"
          options={[
            {
              label: 'Cardholders',
              value: ImporterProcessEnum.CARDHOLDER_LOAD,
            },
            {
              label: 'Tarjetas',
              value: ImporterProcessEnum.CARD_LOAD,
            },
          ]}
        />
      </Form.Item>

      <Dragger {...props}>
        <p className="ant-upload-drag-icon">
          <InboxOutlined />
        </p>
        <p className="ant-upload-text">Click or drag file to this area to upload</p>
      </Dragger>


      <SubmitButton form={form} loading={isLoading} />
    </Form>
  )
}



/*
*   Page
*/

type SearchBulkLoadStatusFilters = {
  processStatus: string;
  dateRanges: [Dayjs, Dayjs];
};

const BulkLoadFilters = ({ onSubmit }: { onSubmit: (data: SearchBulkLoadStatusFilters) => void }) => {
  const [form] = Form.useForm();

  const formStyle: React.CSSProperties = {
    maxWidth: 'none',
  };

  const onFinish = (values: SearchBulkLoadStatusFilters) => {
    onSubmit(values);
  };

  const onReset = () => {
    onSubmit({});
  };

  return (
    <Form
      form={form}
      name="advanced_search"
      style={formStyle}
      onFinish={onFinish}
      onReset={onReset}
      initialValues={{ processType: ProcessStatus.PENDING }}
    >
      <Row gutter={24}>
        <Col span={8}>
          <Form.Item<SearchBulkLoadStatusFilters> name="processStatus" label="Status">
            <Select
                options={[
                  {
                    label: 'Pendiente',
                    value: ProcessStatus.PENDING,
                  },
                  {
                    label: 'Procesando',
                    value: ProcessStatus.PROCESSING,
                  },
                  {
                    label: 'Procesado',
                    value: ProcessStatus.PROCESSED,
                  },
                  {
                    label: 'Error',
                    value: ProcessStatus.ERROR,
                  }
                ]}
              />
          </Form.Item>
        </Col>
        <Col span={8}>
          <Form.Item<SearchBulkLoadStatusFilters> name="dateRanges" label="Fechas">
            <RangePicker style={{ width: '100%' }} />
          </Form.Item>
        </Col>
          <Col span={8} style={{ marginBottom: '1.5rem' }}>
              <div style={{ textAlign: 'right' }}>
                <Space size="small">
                  <Button htmlType="reset">Limpiar</Button>
                  <Button type="primary" htmlType="submit">
                    Buscar
                  </Button>
                </Space>
              </div>
         </Col>
      </Row>
    </Form>
  );
};

const BulkLoad = () => {
  const navigate = useNavigate();
  const { id, issuer } = useParams<{ id: string; issuer: string }>();

  const [newBulkLoadModalOpen, setNewBulkLoadModalOpen] = useState<boolean>(false);

  const NewBulkLoadAction = () => {
    return (
      <Button icon={<UploadOutlined />} onClick={() => setNewBulkLoadModalOpen(true)}  type="primary">
        Subir
      </Button>
    );
  };

  const [filters, setFilters] = useState({
    processStatus: null,
    startDate: '',
    endDate: '',
  });

  const handlePaginationChange = (page: number, pageSize: number) => {
    setPaginationModel({ page: page - 1, pageSize });
  };

  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 10,
  });

  const { processes, isLoading, execute: retryGetBulkLoadStatus } = useGetBulkLoadStatus(
    paginationModel.page,
    paginationModel.pageSize,
    filters.processStatus,
    filters.startDate,
    filters.endDate
  );


  const handleSearchClick = (data: SearchBulkLoadStatusFilters) => {
    setFilters({
      processStatus: ProcessStatus[data.processStatus],
      startDate: data.dateRanges?.[0] ? `${format(data.dateRanges?.[0]?.toDate(), 'yyyy-MM-dd')}T00:00:00` : '',
      endDate: data.dateRanges?.[1] ? `${format(data.dateRanges?.[1]?.toDate(), 'yyyy-MM-dd')}T00:00:00` : '',
    });
  };

  const columns: ColumnsType<IProcessorStatus> = [
    {
      dataIndex: 'id',
      key: 'id',
      title: 'Id',
    },
    {
      dataIndex: 'file_name',
      key: 'file_name',
      title: 'Nombre Archivo',
    },
    {
      dataIndex: 'process_number',
      key: 'process_number',
      title: 'Nro Proceso',
    },
    {
      dataIndex: 'process_status',
      key: 'process_status',
      title: 'Estado',
    },
    {
      dataIndex: 'process_type',
      key: 'process_type',
      title: 'Tipo',
    },
    {
      dataIndex: 'process_date',
      key: 'process_date',
      title: 'Fecha',
      render: (_, process) => {
        return getProperDate(process.process_date);
      }
    }
  ];

  return (
    <PageWithFilters title="Altas Masivas">
      <PageWithFiltersContentContainer>
        <BulkLoadFilters onSubmit={handleSearchClick} />
      </PageWithFiltersContentContainer>
      <PageWithFiltersContentContainer>
        <BulkLoadActionsContainer>
            <NewBulkLoadAction/>
        </BulkLoadActionsContainer>
        <Table
          dataSource={processes}
          scroll={{ x: true }}
          columns={columns}
          loading={isLoading}
          pagination={{
            total: processes?.length,
            defaultPageSize: 10,
            defaultCurrent: 1,
            onChange: handlePaginationChange,
            pageSizeOptions: ['10', '25', '50', '100'],
            showSizeChanger: true
          }}
        />
      </PageWithFiltersContentContainer>
      <Modal
        open={newBulkLoadModalOpen}
        onCancel={() => setNewBulkLoadModalOpen(false)}
        footer={null}
        width={500}
      >
        <NewBulkLoadForm onCancel={() => setNewBulkLoadModalOpen(false)} onSubmit={() => retryGetBulkLoadStatus()}/>
      </Modal>
    </PageWithFilters>
  );
};

export default BulkLoad;
